// The immersive enterprise chrome for /simulations/:slug/*: a Jira-style top bar
// + left sidebar wrapping the routed page (board / ticket / wiki / dashboard) via
// <Outlet>. Deliberately does NOT render the Camp Apex Navbar/Footer (App.js
// hides those on /simulations) so the whole surface reads as a separate tool.
//
// This is also the SimulationProvider host: it fetches the simulation config
// once from the API, applies its theme as CSS-variable overrides, and exposes
// everything (company, people, labels, columns) to children via context.
import { useEffect } from "react";
import { NavLink, Outlet, Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiTrello, FiBookOpen, FiHome, FiArrowLeft, FiAlertTriangle } from "react-icons/fi";
import { getSimulation } from "../../features/simulation/simulationSlice";
import { SimulationContext, useSimulationValue } from "./SimulationContext";
import "./mithra.css";

function SideItem({ to, end, icon, children }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `mi-navitem ${isActive ? "active" : ""}`}>
      {icon}
      <span>{children}</span>
    </NavLink>
  );
}

// Map the simulation's theme object onto the .mithra CSS variables. Anything not
// set falls back to the stylesheet defaults (the lapis-blue theme).
function themeVars(theme) {
  const vars = {};
  if (!theme) return vars;
  if (theme.primary) vars["--mi-primary"] = theme.primary;
  if (theme.primaryDark || theme.accent)
    vars["--mi-primary-dark"] = theme.primaryDark || theme.accent;
  if (theme.nav) vars["--mi-nav"] = theme.nav;
  return vars;
}

function MithraShell() {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const { simulation, isLoadingSimulation, sessionExpired } = useSelector(
    (s) => s.simulation
  );

  useEffect(() => {
    dispatch(getSimulation(slug));
  }, [dispatch, slug]);

  // Only trust the config once it matches the slug in the URL.
  const loaded = simulation && simulation.slug === slug ? simulation : null;
  const ctx = useSimulationValue(slug, loaded);
  const base = `/simulations/${slug}`;

  if (!loaded) {
    return (
      <div className="mithra">
        <div className="mi-body">
          <main className="mi-content">
            <div className="mi-empty">
              {isLoadingSimulation ? "Loading workspace…" : "Workspace not found."}
            </div>
          </main>
        </div>
      </div>
    );
  }

  const company = ctx.company;
  const wordmark = company.short || ctx.theme.wordmark || company.name || "Workspace";

  return (
    <SimulationContext.Provider value={ctx}>
      <div className="mithra" style={themeVars(ctx.theme)}>
        {/* Banner + top bar share one sticky header so both stay pinned to the
            top of the screen while the workspace scrolls underneath. */}
        <div className="mi-header">
          {/* Disconnected from the Camp Apex login session: board actions and QA
              grading return 401 until the learner re-authenticates. Pinned to the
              top of the screen and points them at login in a new tab so they
              don't lose their place. */}
          {sessionExpired && (
            <div className="mi-session-banner" role="alert">
              <FiAlertTriangle className="mi-session-banner-icon" />
              <span>
                You've been disconnected from your Camp Apex session. Your board
                actions and QA reviews won't work until you log back in.
              </span>
              <a
                href="/auth"
                target="_blank"
                rel="noopener noreferrer"
                className="mi-session-banner-btn"
              >
                Log in to Camp Apex
              </a>
            </div>
          )}

          {/* Top bar */}
          <header className="mi-topbar">
            <Link to={base} className="mi-logo">
              <span className="mi-logo-mark">{wordmark.slice(0, 1)}</span>
              {wordmark}
            </Link>
            <nav className="mi-topnav">
              <NavLink to={`${base}/board`} className={({ isActive }) => (isActive ? "active" : "")}>
                Board
              </NavLink>
              <NavLink to={`${base}/wiki`} className={({ isActive }) => (isActive ? "active" : "")}>
                Wiki
              </NavLink>
            </nav>
            <Link to="/" className="mi-topbar-exit">
              <FiArrowLeft />
              Back to Camp Apex
            </Link>
          </header>
        </div>

        <div className="mi-body">
          {/* Left sidebar */}
          <aside className="mi-sidebar">
            <div className="mi-sidebar-project">
              <span className="mi-logo-mark" style={{ width: 30, height: 30 }}>
                {(company.projectKey || "P").slice(0, 1)}
              </span>
              <div>
                <div className="name">{company.projectName}</div>
                {/* <div className="sub">Software project</div> */}
              </div>
            </div>

            {/* <SideItem to={base} end icon={<FiHome />}>
              Your work
            </SideItem> */}
            <SideItem to={`${base}/board`} icon={<FiTrello />}>
              Board
            </SideItem>

            <div className="mi-nav-section">Docs</div>
            <SideItem to={`${base}/wiki`} icon={<FiBookOpen />}>
              Engineering wiki
            </SideItem>
          </aside>

          <main className="mi-content">
            <Outlet />
          </main>
        </div>
      </div>
    </SimulationContext.Provider>
  );
}

export default MithraShell;
